import { useCallback, useMemo, useState } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { AdminCapability, AdminRole, AdminSection, AdminUser } from '../../types'
import { useSecurityTeamLoader } from './useSecurityTeam.loader'
import { useSecurityTeamMutations } from './useSecurityTeam.mutations'

type Translator = (key: string, params?: Record<string, string | number>) => string

type Args = {
  token: string | null
  authHeaders: (withJson?: boolean) => Record<string, string>
  t: Translator
}

export const useSecurityTeam = ({ token, authHeaders, t }: Args) => {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [roles, setRoles] = useState<AdminRole[]>([])
  const [capabilities, setCapabilities] = useState<AdminCapability[]>([])
  const [sections, setSections] = useState<AdminSection[]>([])
  const [teamLoading, setTeamLoading] = useState(false)
  const teamStateSetters = useMemo(
    () => ({
      setUsers,
      setRoles,
      setCapabilities,
      setSections,
      setTeamLoading,
    }),
    [],
  )

  const loadTeam = useSecurityTeamLoader({
    token,
    authHeaders,
    t,
    setters: teamStateSetters,
  })

  const ensureAuthenticated = useCallback(() => {
    if (token) return true
    toast.error(t('security.team.loginRequired'))
    return false
  }, [t, token])

  const { createUser, updateUserRoles, createRole } = useSecurityTeamMutations({
    authHeaders,
    t,
    ensureAuthenticated,
    setters: {
      setUsers,
      setRoles,
    },
  })

  const resetTeam = useCallback(() => {
    setUsers([])
    setRoles([])
    setCapabilities([])
    setSections([])
  }, [])

  return {
    users,
    roles,
    capabilities,
    sections,
    teamLoading,
    loadTeam,
    createUser,
    createRole,
    updateUserRoles,
    resetTeam,
  }
}
